import type { HTMLAttributes, ReactNode, ThHTMLAttributes } from "react";
import { ChevronUp } from "lucide-react";
import { cn } from "../../lib/cn";

export function TableShell({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "overflow-x-auto rounded-lg border border-neutral-800 bg-neutral-900/40",
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export function Table({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLTableElement>) {
  return (
    <table className={cn("w-full text-sm", className)} {...rest}>
      {children}
    </table>
  );
}

export function THead({ children }: { children: ReactNode }) {
  return (
    <thead className="bg-neutral-900/80 text-xs uppercase tracking-wide text-neutral-500">
      {children}
    </thead>
  );
}

export function TRow({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLTableRowElement>) {
  return (
    <tr
      className={cn("border-t border-neutral-800/60", className)}
      {...rest}
    >
      {children}
    </tr>
  );
}

export function TCell({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLTableCellElement>) {
  return (
    <td className={cn("px-4 py-3 text-neutral-300", className)} {...rest}>
      {children}
    </td>
  );
}

export function Th({
  className,
  children,
  ...rest
}: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      scope="col"
      className={cn("px-4 py-3 text-left font-medium", className)}
      {...rest}
    >
      {children}
    </th>
  );
}

export function SortableTh({
  label,
  active,
  dir,
  onSort,
  align = "left",
  className,
  ...rest
}: ThHTMLAttributes<HTMLTableCellElement> & {
  label: ReactNode;
  active: boolean;
  dir: "asc" | "desc";
  onSort: () => void;
  align?: "left" | "right";
}) {
  return (
    <th
      scope="col"
      aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
      className={cn(
        "select-none px-4 py-3 font-medium",
        align === "right" ? "text-right" : "text-left",
        className,
      )}
      {...rest}
    >
      <button
        type="button"
        onClick={onSort}
        className={cn(
          "inline-flex items-center gap-1 text-neutral-500 hover:text-neutral-200 focus-visible:text-neutral-100",
          align === "right" && "flex-row-reverse",
        )}
      >
        <span>{label}</span>
        <ChevronUp
          size={12}
          aria-hidden
          className={cn(
            "transition-transform",
            !active && "opacity-0",
            active && dir === "desc" && "rotate-180",
          )}
        />
      </button>
    </th>
  );
}
